import { useState, useCallback } from 'react';
import { useUpdateMemoryBankNote } from 'src/api/core/ai/memory-bank/mutations';
import {
  useMemoryBankNote,
  useGetImproveNoteSuggestion,
} from 'src/api/core/ai/memory-bank/queries';
import { useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type { MemoryBankNote } from '@erp/rpc';
import useAuth from '@/api/auth/useAuth';

export interface UseImproveNoteOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  note?: MemoryBankNote; // Optional note data to avoid fetching
}

export function useImproveNote(
  noteId: string | null,
  options: UseImproveNoteOptions = {},
) {
  // Destructure options to avoid stale closure issues
  const { onSuccess, onError, note: providedNote } = options;
  const { currentUser } = useAuth();
  const [editedImprovedNote, setEditedImprovedNote] = useState('');
  const [isEditingImprovedNote, setIsEditingImprovedNote] = useState(false);

  const { showToast } = useToast();
  const invalidateQueries = useInvalidateRpcQueryFn((orpc) => [
    orpc.memoryBank.list.key(),
    orpc.memoryBank.get.key(),
    orpc.memoryBank.search.key(),
  ]);

  // Fetch the original note only if not provided
  const shouldFetchNote = !providedNote && !!noteId;
  const { data: fetchedNote, isLoading: isLoadingNote } = useMemoryBankNote(
    shouldFetchNote && noteId ? noteId : '',
  );

  // Use provided note or fetched note
  const originalNote = providedNote || fetchedNote;
  const isLoadingOriginalNote = shouldFetchNote ? isLoadingNote : false;

  // Prepare input for improvement query
  const improveInput = noteId ? { noteId } : null;

  // Fetch AI improvement suggestions automatically when noteId is available
  const {
    data: improveResult,
    isPending: isLoadingImprovements,
    error: improveError,
  } = useGetImproveNoteSuggestion(improveInput);

  const updateNoteMutation = useUpdateMemoryBankNote();

  const setEditingImprovedNoteText = useCallback((editing: boolean) => {
    setIsEditingImprovedNote(editing);
  }, []);

  const acceptEdit = useCallback(async () => {
    if (!improveResult || !originalNote) return;

    try {
      const finalNote =
        editedImprovedNote !== ''
          ? editedImprovedNote.trim()
          : improveResult.improvedNote;

      // Check if user actually made changes to the AI suggestion
      const userMadeEdits = finalNote !== improveResult.improvedNote.trim();

      // Create appropriate edit log context based on whether user made actual edits
      const editLogContext = userMadeEdits
        ? {
            // Combination AI + User edit
            actorType: 'system' as const,
            model: improveResult.model,
            systemComponent: 'AI Note Improvement',
            userId: currentUser?.id,
            userName: currentUser?.name,
            reason: 'AI improvement with user edits applied',
          }
        : {
            // AI only edit
            actorType: 'system' as const,
            model: improveResult.model,
            systemComponent: 'AI Note Improvement',
            reason: 'AI improvements applied',
          };

      await updateNoteMutation.mutateAsync({
        id: originalNote.id,
        data: {
          note: finalNote,
          editLogContext,
        },
      });

      invalidateQueries();

      const message = userMadeEdits
        ? 'Note improved with AI and your edits applied successfully'
        : 'AI improvements applied successfully';

      showToast({
        title: 'Success',
        message,
        color: 'success',
        icon: 'check',
      });

      onSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to apply improvements';
      showToast({
        title: 'Error',
        message: errorMessage,
        color: 'error',
        icon: 'error',
      });
      onError?.(error instanceof Error ? error : new Error(errorMessage));
    }
  }, [
    improveResult,
    originalNote,
    editedImprovedNote,
    currentUser,
    updateNoteMutation,
    invalidateQueries,
    showToast,
    onSuccess,
    onError,
  ]);

  const rejectEdit = useCallback(() => {
    setEditedImprovedNote(improveResult?.improvedNote || '');
    setIsEditingImprovedNote(false);
  }, [improveResult]);

  const handleEditImprovedNote = useCallback(() => {
    // Initialize editedImprovedNote with the current AI result when starting to edit
    if (!editedImprovedNote && improveResult?.improvedNote) {
      setEditedImprovedNote(improveResult.improvedNote);
    }
    setIsEditingImprovedNote(true);
  }, [editedImprovedNote, improveResult]);

  const handleSaveEditedNote = useCallback((newContent: string) => {
    setEditedImprovedNote(newContent);
    setIsEditingImprovedNote(false);
  }, []);

  const handleCancelEditNote = useCallback(() => {
    setEditedImprovedNote(improveResult?.improvedNote || '');
    setIsEditingImprovedNote(false);
  }, [improveResult]);

  const improvedNoteText =
    editedImprovedNote || improveResult?.improvedNote || '';

  // Check if user has made edits to the AI suggestion
  const hasUserEdits =
    improveResult &&
    improvedNoteText.trim() !== improveResult.improvedNote.trim();

  return {
    // Core data
    originalNote,
    improveResult,
    improvedNoteText,
    improveError,

    // States
    isLoadingImprovements,
    isLoadingNote: isLoadingOriginalNote,
    isEditingImprovedNote,
    hasUserEdits,

    // Actions
    setEditingImprovedNoteText,
    acceptEdit,
    rejectEdit,

    // Edit handlers
    handleEditImprovedNote,
    handleSaveEditedNote,
    handleCancelEditNote,

    // Mutation states
    isAccepting: updateNoteMutation.isPending,
  };
}
